import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Settings, Play, Pause, RotateCcw, SkipForward, X, Heart } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const THEMES = {
  neon: { accent: '#FF0088', start: '#00FF00', reset: '#FF0088', next: '#00FFFF', settings: '#FFFF00', divTop: '#00FFFF', divBot: '#FF0088' },
  classic: { accent: '#CC0000', start: '#00AA00', reset: '#CC0000', next: '#0066FF', settings: '#FF8800', divTop: '#0066FF', divBot: '#CC0000' },
  pastel: { accent: '#FF8888', start: '#88FF88', reset: '#FF8888', next: '#88AAFF', settings: '#FFDD88', divTop: '#88AAFF', divBot: '#FF8888' },
};

const DECK_PATTERNS = {
  stars: 'stars',
  diamonds: 'diamonds',
  circles: 'circles',
  leaves: 'leaves',
};

// SVG card back with pattern + BingoGrid AI branding
function CardBack({ width, height, color1, color2, pattern, style, className }) {
  const patternId = `pat-${pattern}-${Math.random().toString(36).substr(2,5)}`;
  let patternSVG;
  const c1 = color1 || '#4a0e78';
  const c2 = color2 || '#7b2faf';

  if (pattern === 'diamonds') {
    patternSVG = (
      <pattern id={patternId} width="20" height="20" patternUnits="userSpaceOnUse">
        <rect width="20" height="20" fill={c1}/>
        <polygon points="10,0 20,10 10,20 0,10" fill={c2} opacity="0.5"/>
        <circle cx="10" cy="10" r="2" fill="#fff" opacity="0.15"/>
      </pattern>
    );
  } else if (pattern === 'circles') {
    patternSVG = (
      <pattern id={patternId} width="16" height="16" patternUnits="userSpaceOnUse">
        <rect width="16" height="16" fill={c1}/>
        <circle cx="8" cy="8" r="6" fill="none" stroke={c2} strokeWidth="2" opacity="0.5"/>
        <circle cx="0" cy="0" r="4" fill={c2} opacity="0.2"/>
        <circle cx="16" cy="16" r="4" fill={c2} opacity="0.2"/>
      </pattern>
    );
  } else if (pattern === 'leaves') {
    patternSVG = (
      <pattern id={patternId} width="18" height="24" patternUnits="userSpaceOnUse">
        <rect width="18" height="24" fill={c1}/>
        <ellipse cx="9" cy="8" rx="5" ry="8" fill={c2} opacity="0.35" transform="rotate(15,9,8)"/>
        <ellipse cx="9" cy="20" rx="5" ry="8" fill={c2} opacity="0.25" transform="rotate(-15,9,20)"/>
      </pattern>
    );
  } else {
    // stars (default)
    patternSVG = (
      <pattern id={patternId} width="20" height="20" patternUnits="userSpaceOnUse">
        <rect width="20" height="20" fill={c1}/>
        <polygon points="10,2 12,8 18,8 13,12 15,18 10,14 5,18 7,12 2,8 8,8" fill={c2} opacity="0.4"/>
      </pattern>
    );
  }

  return (
    <svg width={width || '100%'} height={height || '100%'} viewBox="0 0 120 180" className={className} style={style} xmlns="http://www.w3.org/2000/svg">
      <defs>{patternSVG}</defs>
      <rect x="0" y="0" width="120" height="180" rx="10" ry="10" fill={c1}/>
      <rect x="4" y="4" width="112" height="172" rx="8" ry="8" fill={`url(#${patternId})`}/>
      <rect x="4" y="4" width="112" height="172" rx="8" ry="8" fill="none" stroke="#fff" strokeWidth="1" opacity="0.2"/>
      <rect x="8" y="8" width="104" height="164" rx="6" ry="6" fill="none" stroke="#fff" strokeWidth="0.5" opacity="0.15"/>
      {/* BingoGrid AI text */}
      <text x="60" y="88" textAnchor="middle" fontFamily="Arial Black, Arial, sans-serif" fontWeight="900" fontSize="11" fill="#000" opacity="0.85">BingoGrid</text>
      <text x="60" y="102" textAnchor="middle" fontFamily="Arial Black, Arial, sans-serif" fontWeight="900" fontSize="14" fill="#cc0000" opacity="0.9">AI</text>
    </svg>
  );
}

export default function LoteriaCallerDraw({ onAutoMarkup, onReset, className, figures: figuresProp }) {
  const [figures, setFigures] = useState([]);
  const [calledCards, setCalledCards] = useState([]);
  const [currentCard, setCurrentCard] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(5);
  const [theme, setTheme] = useState('neon');
  const [showSettings, setShowSettings] = useState(false);
  const [settingsTab, setSettingsTab] = useState('appearance');
  const [topColor, setTopColor] = useState('#1a0a2e');
  const [bottomColor, setBottomColor] = useState('#0a0a0a');
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [selectedVoice, setSelectedVoice] = useState('');
  const [availableVoices, setAvailableVoices] = useState([]);
  const [favoriteVoices, setFavoriteVoices] = useState([]);
  const [isShuffling, setIsShuffling] = useState(false);
  const [shufflePhase, setShufflePhase] = useState(0);
  const [shuffleCard, setShuffleCard] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false); // card flip animation
  const [deckColor1, setDeckColor1] = useState('#4a0e78');
  const [deckColor2, setDeckColor2] = useState('#7b2faf');
  const [deckPattern, setDeckPattern] = useState('stars');
  const [showFullHistory, setShowFullHistory] = useState(false);
  const timerRef = useRef(null);
  const shuffleTimerRef = useRef(null);
  const drawAnimRef = useRef(null);

  const colors = THEMES[theme];

  useEffect(() => {
    // If parent passes figures (current deck choice), use them. Otherwise
    // fall back to fetching the user's original uploaded deck.
    if (figuresProp && figuresProp.length > 0) {
      setFigures(figuresProp);
      return;
    }
    fetch(`${API}/api/loteria-figures`)
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setFigures(data); })
      .catch(() => {});
  }, [figuresProp]);

  const selectedVoiceRef = useRef(selectedVoice);
  selectedVoiceRef.current = selectedVoice;

  useEffect(() => {
    const loadVoices = () => {
      const voices = window.speechSynthesis?.getVoices() || [];
      const esOnly = voices.filter(v => v.lang.startsWith('es'));
      setAvailableVoices(esOnly.length > 0 ? esOnly : []);
      if (!selectedVoiceRef.current && esOnly.length > 0) {
        const natural = esOnly.find(v => v.name.toLowerCase().includes('natural')) || esOnly[0];
        setSelectedVoice(natural.name);
      }
    };
    loadVoices();
    window.speechSynthesis?.addEventListener('voiceschanged', loadVoices);
    return () => window.speechSynthesis?.removeEventListener('voiceschanged', loadVoices);
  }, []);

  const remaining = useMemo(() => {
    const calledIds = new Set(calledCards.map(c => c.id));
    return figures.filter(f => !calledIds.has(f.id));
  }, [figures, calledCards]);

  const speak = useCallback((text) => {
    if (!voiceEnabled || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    const voice = availableVoices.find(v => v.name === selectedVoice);
    if (voice) u.voice = voice;
    u.rate = 0.9;
    u.pitch = 1;
    window.speechSynthesis.speak(u);
  }, [voiceEnabled, selectedVoice, availableVoices]);

  const getDisplayName = (name) => { const m = name.match(/^\d+\s*-\s*(.+)$/); return m ? m[1] : name; };
  // Number for callouts: "1 - El Gallo" → "1", or for plain names ("El Gallo")
  // derive from index in `figures` (1-based). Falls back to '' if not found.
  const getNumber = (cardOrName) => {
    if (!cardOrName) return '';
    if (typeof cardOrName === 'string') {
      const m = cardOrName.match(/^(\d+)/);
      return m ? m[1] : '';
    }
    const m = cardOrName.name && cardOrName.name.match(/^(\d+)/);
    if (m) return m[1];
    const idx = figures.findIndex(f => f.id === cardOrName.id);
    return idx >= 0 ? String(idx + 1) : '';
  };

  const drawCard = useCallback(() => {
    if (remaining.length === 0) {
      setIsPlaying(false);
      if (timerRef.current) clearInterval(timerRef.current);
      return null;
    }
    const idx = Math.floor(Math.random() * remaining.length);
    const card = remaining[idx];

    // Trigger draw animation: card slides from deck to face-up
    setIsDrawing(true);
    if (drawAnimRef.current) clearTimeout(drawAnimRef.current);
    drawAnimRef.current = setTimeout(() => setIsDrawing(false), 400);

    // Card flip sound
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const buf = ctx.createBuffer(1, ctx.sampleRate * 0.15, ctx.sampleRate);
      const d = buf.getChannelData(0);
      for (let i = 0; i < d.length; i++) {
        const t = i / ctx.sampleRate;
        d[i] = (Math.random() * 2 - 1) * 0.08 * (1 - t / 0.15);
      }
      const s = ctx.createBufferSource();
      s.buffer = buf;
      const f = ctx.createBiquadFilter();
      f.type = 'highpass';
      f.frequency.value = 2000;
      s.connect(f);
      f.connect(ctx.destination);
      s.start();
      s.onended = () => ctx.close();
    } catch (e) {}

    setCurrentCard(card);
    setCalledCards(prev => [...prev, card]);
    speak(getDisplayName(card.name));
    if (onAutoMarkup) onAutoMarkup(card);
    return card;
  }, [remaining, speak, onAutoMarkup]);

  const handleStart = useCallback(() => {
    if (isPlaying) {
      setIsPlaying(false);
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    setIsPlaying(true);
    drawCard();
    timerRef.current = setInterval(() => drawCard(), speed * 1000);
  }, [isPlaying, drawCard, speed]);

  const handleNext = useCallback(() => { drawCard(); }, [drawCard]);

  const handleReset = useCallback(() => {
    setIsPlaying(false);
    if (timerRef.current) clearInterval(timerRef.current);
    window.speechSynthesis?.cancel();
    setCalledCards([]);
    setCurrentCard(null);
    if (onReset) onReset();
    setIsShuffling(true);

    // Riffle shuffle sound
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const duration = 1.8;
      const buf = ctx.createBuffer(1, ctx.sampleRate * duration, ctx.sampleRate);
      const data = buf.getChannelData(0);
      for (let i = 0; i < data.length; i++) {
        const t = i / ctx.sampleRate;
        // Two riffle bursts
        const riffle1 = t < 0.7 ? Math.sin(t * 80) > 0.2 ? 1 : 0.1 : 0;
        const riffle2 = t > 0.9 && t < 1.6 ? Math.sin((t - 0.9) * 90) > 0.2 ? 1 : 0.1 : 0;
        const fade = t < 1.4 ? 1 : 1 - ((t - 1.4) / 0.4);
        data[i] = (Math.random() * 2 - 1) * 0.1 * (riffle1 + riffle2) * Math.max(0, fade);
      }
      const src = ctx.createBufferSource();
      src.buffer = buf;
      const filter = ctx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.value = 800;
      filter.Q.value = 0.8;
      src.connect(filter);
      filter.connect(ctx.destination);
      src.start();
      src.onended = () => ctx.close();
    } catch (e) {}

    // 12 phases: split → riffle → merge → split → riffle → merge
    let phase = 1;
    setShufflePhase(1);
    if (shuffleTimerRef.current) clearInterval(shuffleTimerRef.current);
    shuffleTimerRef.current = setInterval(() => {
      phase++;
      if (phase <= 12) {
        setShufflePhase(phase);
        if (figures.length > 0) {
          setShuffleCard(figures[Math.floor(Math.random() * figures.length)]);
        }
      } else {
        clearInterval(shuffleTimerRef.current);
        shuffleTimerRef.current = null;
        setShufflePhase(0);
        setIsShuffling(false);
        setShuffleCard(null);
      }
    }, 140);
  }, [figures]);

  useEffect(() => {
    if (isPlaying) {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(() => drawCard(), speed * 1000);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [speed, isPlaying, drawCard]);

  const displayCard = isShuffling ? shuffleCard : currentCard;
  const last4 = calledCards.length > 0 ? calledCards.slice(-5, -1).reverse() : [];
  const remainingCount = figures.length - calledCards.length;

  const proxyUrl = (src) => {
    if (!src) return '';
    if (src.startsWith('data:')) return src;
    // Same-origin paths (Enhanced deck served from /loteria/*.webp) load
    // directly. The proxy is only needed for cross-origin URLs that would
    // taint the canvas / fail CORS on direct load.
    if (src.startsWith('/')) return src;
    return `${API}/api/proxy-image?url=${encodeURIComponent(src)}`;
  };

  const lumColor = (hex) => {
    const r = parseInt(hex.slice(1,3),16)||0, g = parseInt(hex.slice(3,5),16)||0, b = parseInt(hex.slice(5,7),16)||0;
    return (r*0.299+g*0.587+b*0.114) > 128 ? '#000' : '#e00';
  };

  // Shuffle: phases 1-4 = split apart, 5-8 = riffle (cards fly between), 9-12 = merge back
  const getDeckShuffleStyle = (deck) => {
    if (!isShuffling || shufflePhase === 0) return {};
    const t = '0.12s ease-in-out';
    const p = shufflePhase;

    if (p <= 4) {
      // Split: decks move apart
      const spread = p * 15;
      return deck === 'left'
        ? { transform: `translateX(-${spread}%) rotate(-${p * 2}deg)`, transition: `transform ${t}` }
        : { transform: `translateX(${spread}%) rotate(${p * 2}deg)`, transition: `transform ${t}` };
    } else if (p <= 8) {
      // Riffle: cards fly back and forth
      const riffleP = p - 4;
      const odd = riffleP % 2 === 1;
      return deck === 'left'
        ? { transform: `translateX(${odd ? '20%' : '-30%'}) translateY(${odd ? '-8%' : '0'}) rotate(${odd ? 3 : -6}deg)`, transition: `transform ${t}` }
        : { transform: `translateX(${odd ? '-20%' : '30%'}) translateY(${odd ? '-8%' : '0'}) rotate(${odd ? -3 : 6}deg)`, transition: `transform ${t}` };
    } else {
      // Merge: come back together
      const merge = 12 - p;
      const spread = merge * 10;
      return deck === 'left'
        ? { transform: `translateX(-${spread}%) rotate(-${merge}deg)`, transition: `transform ${t}` }
        : { transform: `translateX(${spread}%) rotate(${merge}deg)`, transition: `transform ${t}` };
    }
  };

  return (
    <div className={`flex flex-col h-full select-none ${className || ''}`} style={{ background: `linear-gradient(to bottom, ${topColor}, ${bottomColor})` }}>
      <style>{`
        @keyframes cardDrawFromDeck {
          0% { transform: translateX(-80%) rotateY(180deg) scale(0.8); opacity: 0.5; }
          40% { transform: translateX(-30%) rotateY(90deg) scale(0.9); opacity: 0.8; }
          100% { transform: translateX(0) rotateY(0deg) scale(1); opacity: 1; }
        }
      `}</style>
      {/* Controls row */}
      <div className="px-1.5 py-1 flex items-center gap-1.5 flex-shrink-0" style={{ background: topColor }}>
        <span className="font-black text-[10px] flex-shrink-0" style={{ color: lumColor(topColor) }}>LOTERIA</span>
        <span className="font-black text-[9px] flex-shrink-0" style={{ color: lumColor(topColor) }}>{calledCards.length}/{figures.length}</span>

        <div className="flex gap-0.5 flex-shrink-0">
          <button data-testid="loteria-start-btn" onClick={handleStart}
            className="h-7 px-2 rounded font-black text-[9px] text-black flex items-center justify-center gap-0.5 transition-all hover:brightness-110"
            style={{ background: colors.start }}>
            {isPlaying ? <Pause className="w-3 h-3" fill="black" /> : <Play className="w-3 h-3" fill="black" />}
            <span>{isPlaying ? 'Pausa' : 'Comenzar'}</span>
          </button>
          <button data-testid="loteria-reset-btn" onClick={handleReset}
            className="w-7 h-7 rounded flex items-center justify-center text-black transition-all hover:brightness-110"
            style={{ background: colors.reset }} title="Barajar">
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
          <button data-testid="loteria-next-btn" onClick={handleNext}
            className="h-7 px-2 rounded font-black text-[9px] text-black flex items-center justify-center gap-0.5 transition-all hover:brightness-110"
            style={{ background: colors.next }}>
            <SkipForward className="w-3 h-3" />
            <span>Siguiente</span>
          </button>
        </div>

        <div className="flex-1" />

        <button data-testid="loteria-settings-btn" onClick={() => setShowSettings(true)}
          className="w-6 h-6 rounded flex items-center justify-center text-black transition-all hover:brightness-110 flex-shrink-0"
          style={{ background: colors.next }}>
          <Settings className="w-3 h-3" />
        </button>
      </div>

      <div className="h-px flex-shrink-0" style={{ background: `linear-gradient(to right, ${colors.divTop}, ${colors.divBot})` }} />

      {/* Main content: face-down deck | face-up card | fanned history */}
      <div className="flex-1 flex overflow-hidden min-h-0 relative" style={{ background: bottomColor }}>

        {/* Flying card during shuffle (alternates between decks) */}
        {isShuffling && shufflePhase >= 5 && shufflePhase <= 8 && (
          <div className="absolute z-30 rounded-xl" style={{
            width: '15%', aspectRatio: '2/3',
            top: '10%',
            left: shufflePhase % 2 === 1 ? '15%' : '50%',
            transition: 'left 0.1s ease-in-out',
          }}>
            <CardBack width="100%" height="100%" color1={deckColor1} color2={deckColor2} pattern={deckPattern} className="rounded-xl" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.7)' }} />
          </div>
        )}

        {/* Face-down deck (uncalled) — left half */}
        <div className="flex items-center justify-center" style={{ flex: '0 0 25%' }}>
          <div className="relative h-[80%]" style={{ aspectRatio: '2/3', ...getDeckShuffleStyle('left') }}>
            {/* Stacked deck layers */}
            <div className="absolute inset-0 rounded-xl" style={{ transform: 'translate(4px, 4px)', opacity: 0.25 }}>
              <CardBack width="100%" height="100%" color1={deckColor1} color2={deckColor2} pattern={deckPattern} className="rounded-xl" />
            </div>
            <div className="absolute inset-0 rounded-xl" style={{ transform: 'translate(2px, 2px)', opacity: 0.5 }}>
              <CardBack width="100%" height="100%" color1={deckColor1} color2={deckColor2} pattern={deckPattern} className="rounded-xl" />
            </div>
            <div className="relative rounded-xl" style={{ width: '100%', height: '100%', boxShadow: '3px 4px 14px rgba(0,0,0,0.7)' }}>
              <CardBack width="100%" height="100%" color1={deckColor1} color2={deckColor2} pattern={deckPattern} className="rounded-xl" />
            </div>
            <div className="absolute bottom-1 left-0 right-0 text-center">
              <span className="text-[8px] font-bold text-white/60">{remainingCount}</span>
            </div>
          </div>
        </div>

        {/* Face-up current card — center, with draw animation */}
        <div className="flex items-center justify-center relative" style={{ flex: '1 1 43%' }}>
          {displayCard ? (
            <div className="relative h-[90%]" style={{
              aspectRatio: '2/3',
              ...getDeckShuffleStyle('right'),
              ...(isDrawing && !isShuffling ? {
                animation: 'cardDrawFromDeck 0.4s ease-out',
              } : {}),
            }}>
              <img
                src={proxyUrl(displayCard.src)}
                alt={displayCard.name}
                className="w-full h-full object-cover rounded-xl"
                style={{ boxShadow: `0 0 20px ${colors.accent}33, 3px 4px 14px rgba(0,0,0,0.6)` }}
                crossOrigin="anonymous"
              />
            </div>
          ) : (
            <div className="text-gray-500 text-sm font-bold text-center" style={getDeckShuffleStyle('right')}>
              <p className="text-gray-400">Baraja de Loteria</p>
              <p className="text-[10px] mt-1 text-gray-600">Presiona Comenzar o Siguiente</p>
            </div>
          )}
        </div>

        {/* Fanned history — right */}
        <div className="flex items-center justify-start cursor-pointer overflow-hidden"
          style={{ flex: '0 0 28%', marginLeft: '-2%' }}
          onClick={() => calledCards.length > 1 && setShowFullHistory(true)}
          data-testid="loteria-history-fan"
          title={calledCards.length > 1 ? 'Ver todas las cartas cantadas' : ''}>
          {last4.length > 0 ? (
            <div className="relative w-full" style={{ height: '55%' }}>
              {last4.map((c, i) => {
                const total = last4.length;
                return (
                  <div key={c.id + '-' + i}
                    className="absolute overflow-hidden transition-all duration-300 rounded-xl"
                    style={{
                      left: `${i * 17}%`,
                      top: '0',
                      height: '100%',
                      width: '45%',
                      opacity: 1 - i * 0.2,
                      zIndex: total - i,
                    }}>
                    <img
                      src={proxyUrl(c.src)}
                      alt={c.name}
                      className="w-full h-full object-cover rounded-xl"
                      style={{ boxShadow: '4px 4px 12px rgba(0,0,0,0.6)' }}
                      crossOrigin="anonymous"
                    />
                  </div>
                );
              })}
            </div>
          ) : (
            <span className="text-[9px] text-gray-600 font-bold">Anteriores</span>
          )}
        </div>
      </div>

      {/* Full History Modal */}
      {showFullHistory && (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/70 backdrop-blur-sm"
          onClick={() => setShowFullHistory(false)}>
          <div className="w-[500px] max-h-[85vh] overflow-y-auto rounded-xl p-4"
            style={{ background: '#0d0d1a', border: `2px solid ${colors.accent}`, boxShadow: `0 0 30px ${colors.accent}33` }}
            onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-lg font-black" style={{ color: colors.accent }}>Cartas Cantadas ({calledCards.length})</h2>
              <button onClick={() => setShowFullHistory(false)} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-5 gap-2">
              {[...calledCards].reverse().map((c, i) => (
                <div key={c.id + '-hist-' + i} className="flex flex-col items-center">
                  <img src={proxyUrl(c.src)} alt={c.name} className="w-full object-contain rounded-xl" style={{ border: '1px solid #333' }} crossOrigin="anonymous" />
                  <span className="text-[8px] font-bold text-gray-400 mt-0.5 truncate w-full text-center">{getNumber(c)} - {getDisplayName(c.name)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => setShowSettings(false)}>
          <div className="w-[420px] max-h-[80vh] overflow-y-auto rounded-xl p-5"
            style={{ background: '#0d1929', border: `2px solid ${colors.divTop}`, boxShadow: `0 0 30px ${colors.divTop}33` }}
            onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-black italic" style={{ color: colors.divTop }}>Ajustes</h2>
              <button onClick={() => setShowSettings(false)} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            <div className="flex rounded-lg overflow-hidden mb-5" style={{ border: `1px solid ${colors.divTop}44`, background: '#1a2a40' }}>
              {['appearance', 'deck', 'background', 'voice'].map(tab => (
                <button key={tab} onClick={() => setSettingsTab(tab)}
                  className={`flex-1 py-2 text-xs font-bold capitalize transition-colors ${settingsTab === tab ? 'bg-white text-black' : 'text-gray-400 hover:text-white'}`}>
                  {tab === 'appearance' ? 'Apariencia' : tab === 'deck' ? 'Baraja' : tab === 'background' ? 'Fondo' : 'Voz'}
                </button>
              ))}
            </div>

            {settingsTab === 'appearance' && (
              <div className="space-y-5">
                <div>
                  <h3 className="text-white font-black text-sm mb-3">TEMPORIZADOR</h3>
                  <div className="flex items-center gap-3">
                    <input type="range" min={1} max={15} step={0.5} value={speed} onChange={(e) => setSpeed(Number(e.target.value))} className="flex-1 h-2 cursor-pointer accent-white" />
                    <span className="text-white font-black text-sm w-10 text-right">{speed}s</span>
                  </div>
                </div>
                <div>
                  <h3 className="text-white font-black text-sm mb-3">TEMA DE COLOR</h3>
                  <div className="grid grid-cols-3 gap-2">
                    {Object.keys(THEMES).map(t => (
                      <button key={t} onClick={() => setTheme(t)}
                        className={`py-3 rounded-lg font-bold text-sm capitalize transition-all ${theme === t ? 'text-black' : 'text-gray-400 hover:text-white'}`}
                        style={{ background: theme === t ? THEMES[t].next : '#1a2a40', border: `1px solid ${theme === t ? THEMES[t].next : '#333'}` }}>{t}</button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {settingsTab === 'deck' && (
              <div className="space-y-5">
                <div>
                  <h3 className="text-white font-black text-sm mb-3">COLOR PRINCIPAL</h3>
                  <div className="flex items-center gap-2">
                    <input type="color" value={deckColor1} onChange={(e) => setDeckColor1(e.target.value)} className="w-10 h-10 cursor-pointer rounded border-2 border-gray-600" />
                    <span className="text-gray-400 text-sm font-mono">{deckColor1}</span>
                  </div>
                </div>
                <div>
                  <h3 className="text-white font-black text-sm mb-3">COLOR PATRON</h3>
                  <div className="flex items-center gap-2">
                    <input type="color" value={deckColor2} onChange={(e) => setDeckColor2(e.target.value)} className="w-10 h-10 cursor-pointer rounded border-2 border-gray-600" />
                    <span className="text-gray-400 text-sm font-mono">{deckColor2}</span>
                  </div>
                </div>
                <div>
                  <h3 className="text-white font-black text-sm mb-3">DISENO</h3>
                  <div className="grid grid-cols-4 gap-2">
                    {Object.keys(DECK_PATTERNS).map(p => (
                      <button key={p} onClick={() => setDeckPattern(p)}
                        className={`py-2 rounded-lg text-xs font-bold capitalize transition-all ${deckPattern === p ? 'text-black' : 'text-gray-400 hover:text-white'}`}
                        style={{ background: deckPattern === p ? colors.next : '#1a2a40', border: `1px solid ${deckPattern === p ? colors.next : '#333'}` }}>{p === 'stars' ? 'Estrellas' : p === 'diamonds' ? 'Diamantes' : p === 'circles' ? 'Circulos' : 'Hojas'}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="text-white font-black text-xs mb-2">VISTA PREVIA</h3>
                  <div className="flex justify-center"><CardBack width="80" height="120" color1={deckColor1} color2={deckColor2} pattern={deckPattern} /></div>
                </div>
              </div>
            )}

            {settingsTab === 'background' && (
              <div className="space-y-4">
                <div>
                  <h3 className="text-white font-black text-xs mb-2">COLOR SUPERIOR</h3>
                  <div className="flex items-center gap-2">
                    <input type="color" value={topColor} onChange={(e) => setTopColor(e.target.value)} className="w-10 h-10 cursor-pointer rounded border-2 border-gray-600" />
                    <span className="text-gray-400 text-sm font-mono">{topColor}</span>
                  </div>
                </div>
                <div>
                  <h3 className="text-white font-black text-xs mb-2">COLOR INFERIOR</h3>
                  <div className="flex items-center gap-2">
                    <input type="color" value={bottomColor} onChange={(e) => setBottomColor(e.target.value)} className="w-10 h-10 cursor-pointer rounded border-2 border-gray-600" />
                    <span className="text-gray-400 text-sm font-mono">{bottomColor}</span>
                  </div>
                </div>
              </div>
            )}

            {settingsTab === 'voice' && (
              <div className="space-y-4">
                <div>
                  <h3 className="text-white font-black text-xs mb-2">MODO DE VOZ</h3>
                  <select value={voiceEnabled ? 'tts' : 'off'} onChange={(e) => setVoiceEnabled(e.target.value === 'tts')}
                    className="w-full p-2.5 rounded-lg text-sm text-white" style={{ background: '#1a2a40', border: `1px solid ${colors.divTop}44` }}>
                    <option value="tts">Voz del Navegador</option>
                    <option value="off">Desactivado</option>
                  </select>
                </div>
                {voiceEnabled && (
                  <>
                    <div>
                      <h3 className="text-white font-black text-xs mb-2">SELECCIONAR VOZ (ESPANOL)</h3>
                      <select value={selectedVoice} onChange={(e) => setSelectedVoice(e.target.value)}
                        className="w-full p-2.5 rounded-lg text-sm text-white" style={{ background: '#1a2a40', border: `1px solid ${colors.divTop}44` }}>
                        {availableVoices.map(v => (<option key={v.name} value={v.name}>{v.name} - {v.lang}</option>))}
                        {availableVoices.length === 0 && <option value="">No hay voces en espanol disponibles</option>}
                      </select>
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <h3 className="text-white font-black text-xs">FAVORITOS</h3>
                        <button onClick={() => { if (selectedVoice && !favoriteVoices.includes(selectedVoice)) setFavoriteVoices(prev => [...prev, selectedVoice]); }}
                          className="px-3 py-1 bg-green-500 text-black text-xs font-bold rounded flex items-center gap-1"><Heart className="w-3 h-3" /> Guardar</button>
                      </div>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {favoriteVoices.map(v => (
                          <div key={v} className="flex items-center gap-2 p-2 rounded" style={{ background: '#1a2a40' }}>
                            <span className="flex-1 text-xs text-gray-300 truncate">{v}</span>
                            <button onClick={() => setSelectedVoice(v)} className="px-2 py-0.5 text-[10px] font-bold rounded" style={{ background: colors.divTop, color: 'black' }}>Cargar</button>
                            <button onClick={() => setFavoriteVoices(prev => prev.filter(f => f !== v))} className="px-2 py-0.5 text-[10px] font-bold rounded bg-red-600 text-white">X</button>
                          </div>
                        ))}
                        {favoriteVoices.length === 0 && <p className="text-gray-500 text-xs">No hay favoritos guardados</p>}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
